import { AppLayout } from 'layouts';
import type { Media, VideoContentObject } from 'models';
import type { ReactNode } from 'react';
import { useGetVideoDetailQuery, useGetVideoMedia } from '../queries';
import { useIsSkeletonVisible } from './hooks';
import { VideoDetailSkeleton } from './video-detail-skeleton';

type P = {
  children: (data: VideoDetailData) => ReactNode;
  header: (data: VideoDetailData) => ReactNode;
  id: string;
};

type VideoDetailData = {
  fullData?: Media;
  trailerData?: Media;
  videoContentObject: VideoContentObject;
};

const VideoDetailLayout = ({ children, header, id }: P) => {
  const { data: videoContentObject, error, isLoading: isContentObjectLoading } = useGetVideoDetailQuery(id);

  const isDraft = videoContentObject?.video.status === 'draft';

  const {
    data: trailerData,
    isFetching: isFetchingTrailer,
    isLoading: isLoadingTrailer,
  } = useGetVideoMedia({
    mediaId: videoContentObject?.video.trailer?.externalId,
    options: {
      staleTime: isDraft ? 0 : 5 * 60 * 1000,
    },
  });

  // TODO: consider load only when someone is subscribed
  const {
    data: fullData,
    isFetching: isFetchingFull,
    isLoading: isLoadingFull,
  } = useGetVideoMedia({
    mediaId: videoContentObject?.video.full?.externalId,
    options: {
      staleTime: isDraft ? 0 : 5 * 60 * 1000,
    },
  });

  const isSkeletonVisible = useIsSkeletonVisible({
    isContentObjectLoading,
    isFetchingFull,
    isFetchingTrailer,
    isLoadingFull,
    isLoadingTrailer,
    videoContentObject,
  });

  return (
    <AppLayout error={error}>
      {isSkeletonVisible || !videoContentObject ? (
        <VideoDetailSkeleton />
      ) : (
        <>
          {header({ fullData, trailerData, videoContentObject })}
          {children({ fullData, trailerData, videoContentObject })}
        </>
      )}
    </AppLayout>
  );
};

export default VideoDetailLayout;
